import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Col,
  Container,
  Row,
  Spinner,
} from "react-bootstrap";
import { Circle, Group, Line, Rect, Shape, Stage, Layer, Text } from "react-konva";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams } from "react-router-dom";
import nipplejs from "nipplejs";

import FieldSubmitForm from "../Field/FieldSubmitForm";
import Preloader from "@/components/Elements/Preloader";
import { camerasApi } from "@/api";

const DEFAULT_FIELD_POINTS = [
  { id: "0", x: 0.1, y: 0.25, color: "#6200EE", desc: "Top Left" },
  { id: "1", x: 0.5, y: 0.25, color: "#03DAc6", desc: "Top Middle" },
  { id: "2", x: 0.9, y: 0.25, color: "#B00020", desc: "Top Right" },
  { id: "3", x: 0.9, y: 0.75, color: "#FFDE03", desc: "Bottom Right" },
  { id: "4", x: 0.5, y: 0.75, color: "#61D800", desc: "Bottom Middle" },
  { id: "5", x: 0.1, y: 0.75, color: "#90CAF9", desc: "Bottom Left" },
  { id: "6", x: 0.1, y: 0.5, color: "#90CAF9", desc: "Middle Left" },
  { id: "7", x: 0.5, y: 0.5, color: "#90CAF9", desc: "Middle" },
  { id: "8", x: 0.9, y: 0.5, color: "#90CAF9", desc: "Middle Right" },
];

const STICK_FORCE = 0.05;

function makePoints(defs) {
  return defs.map((p) => ({ ...p, isDragging: false }));
}

function restorePoints(saved, defaults) {
  const result = [];
  for (const src of saved) {
    const def = defaults.find((d) => d.id === src.id);
    if (def) result.push({ ...def, ...src, isDragging: false });
  }
  for (const def of defaults) {
    if (!result.find((p) => p.id === def.id)) {
      result.push({ ...def, isDragging: false });
    }
  }
  return result;
}

export default function PlaygroundFieldPage() {
  const { playgroundId } = useParams();
  const containerRef = useRef();
  const formRef = useRef();
  const nippleRef = useRef();
  const stickRef = useRef({ x: 0, y: 0 });
  const managerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [field, setField] = useState({ width: 0, height: 0 });
  const [points, setPoints] = useState(() => makePoints(DEFAULT_FIELD_POINTS));
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Container dimensions for the Konva stage
  const [stageW, setStageW] = useState(0);
  const [stageH, setStageH] = useState(0);

  // ── Load field info ──────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    camerasApi.getFieldInfo(playgroundId).then((res) => {
      if (cancelled) return;
      const { field: f } = res;
      setField(f);
      if (f.points?.length > 0) {
        setPoints(restorePoints(f.points, DEFAULT_FIELD_POINTS));
      }
      setTimeout(() => {
        formRef.current?.onValuesUpdate({
          width: f.width,
          height: f.height,
        });
      }, 0);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [playgroundId]);

  // ── Load snapshot ────────────────────────────────────────────────────
  const loadSnapshot = useCallback(async () => {
    setSnapshotLoading(true);
    try {
      const blob = await camerasApi.getSnapshot(playgroundId);
      if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
      setSnapshotUrl(URL.createObjectURL(blob));
    } catch {
      alert("Failed to load snapshot");
    } finally {
      setSnapshotLoading(false);
    }
  }, [playgroundId, snapshotUrl]);

  useEffect(() => {
    loadSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playgroundId]);

  // ── Resize observer for container ────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      setStageW(w);
      setStageH(w * 9 / 16);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  // ── Nipplejs (virtual joystick) ────────────────────────────────────
  useEffect(() => {
    const el = nippleRef.current;
    if (!el || !editing) return;

    const manager = nipplejs.create({
      zone: el,
      mode: "dynamic",
      color: "#ffffff",
      size: 100,
    });
    managerRef.current = manager;

    let interval;
    manager.on("added", () => {
      interval = setInterval(() => {
        const stick = stickRef.current;
        if (stick.x === 0 && stick.y === 0) return;
        setPoints((prev) =>
          prev.map((p) => {
            // Use functional form — `active` is read from DOM via a ref-like trick:
            // We store activeId in a ref so the interval always has the latest value.
            if (p.id !== activeRef.current) return p;
            return { ...p, x: p.x + stick.x, y: p.y + stick.y };
          })
        );
      }, 25);
    });
    manager.on("move", (_e, stick) => {
      const container = containerRef.current;
      if (!container) return;
      stickRef.current = {
        x: +((stick.distance * Math.cos(stick.angle.radian)) / container.offsetWidth * STICK_FORCE),
        y: +((stick.distance * Math.sin(stick.angle.radian)) / container.offsetHeight * STICK_FORCE * -1),
      };
    });
    manager.on("end", () => {
      clearInterval(interval);
      stickRef.current = { x: 0, y: 0 };
    });

    return () => {
      clearInterval(interval);
      manager.destroy();
      managerRef.current = null;
    };
  }, [editing]);

  // Keep refs in sync so intervals / effects always read the latest values
  const activeRef = useRef(active);
  useEffect(() => { activeRef.current = active; }, [active]);

  const pointsRef = useRef(points);
  useEffect(() => { pointsRef.current = points; }, [points]);

  // ── Toggle nipple pointer-events: off near points / during drag ────
  useEffect(() => {
    const el = containerRef.current;
    const nip = nippleRef.current;
    if (!el || !nip || !editing) return;

    const handler = (e) => {
      const rect = el.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width;
      const ny = (e.clientY - rect.top) / rect.height;
      const hitRadius = 25 / rect.width;
      const pts = pointsRef.current;

      const isDragging = pts.some((p) => p.isDragging);
      let nearPoint = false;
      for (const p of pts) {
        if (Math.hypot(p.x - nx, p.y - ny) < hitRadius) {
          nearPoint = true;
          break;
        }
      }
      nip.style.pointerEvents = nearPoint || isDragging ? "none" : "auto";
    };

    el.addEventListener("pointermove", handler);
    return () => el.removeEventListener("pointermove", handler);
  }, [editing]);

  // ── Keyboard ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    document.addEventListener("keyup", handler);
    return () => document.removeEventListener("keyup", handler);
  }, []);

  // ── Derived ──────────────────────────────────────────────────────────
  const w = stageW;
  const h = stageH;
  const actualRadius = w ? (w / 1030) * 20 : 20;
  const fontSize = 14;
  const lineWidth = 1;
  const hitWidth = 20;

  const toAbsX = useCallback((x) => x * w, [w]);
  const toAbsY = useCallback((y) => y * h, [h]);

  // ── Pointer normalized (getBoundingClientRect — works outside canvas) ─
  const getPointerNormalized = useCallback((e) => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return {
      x: (e.evt.clientX - rect.left) / rect.width,
      y: (e.evt.clientY - rect.top) / rect.height,
    };
  }, []);

  // ── DOM-level pointer down for point selection ──────────────────────
  const handleContainerPointerDown = useCallback((e) => {
    // Ignore events from the nipple container — nipplejs handles those
    if (nippleRef.current && nippleRef.current.contains(e.target)) return;

    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    const hitRadius = 25 / rect.width;

    // Check if clicking near an existing point
    let closest = null;
    let closestDist = Infinity;
    for (const p of points) {
      const d = Math.hypot(p.x - nx, p.y - ny);
      if (d < closestDist) {
        closestDist = d;
        closest = p;
      }
    }

    if (closest && closestDist < hitRadius) {
      setActive(closest.id);
    } else {
      setActive(null);
    }
  }, [points]);

  // ── Point handlers ───────────────────────────────────────────────────
  const handlePointDragStart = useCallback((e) => {
    const id = e.target.id();
    setPoints((prev) => prev.map((p) => ({ ...p, isDragging: p.id === id })));
    setActive(id);
  }, []);

  const handlePointDragMove = useCallback(
    (e) => {
      const id = e.target.id();
      e.target.x(0);
      e.target.y(0);
      const { x, y } = getPointerNormalized(e);
      setPoints((prev) =>
        prev.map((p) => (p.id === id ? { ...p, x, y } : p))
      );
    },
    [getPointerNormalized]
  );

  const handlePointDragEnd = useCallback((e) => {
    const id = e.target.id();
    setPoints((prev) => prev.map((p) => (p.id === id ? { ...p, isDragging: false } : p)));
  }, []);

  const handlePointClick = useCallback((e) => {
    setActive(e.target.id());
  }, []);

  // ── Orientation / reset ──────────────────────────────────────────────
  const handleSwitchX = useCallback(() => {
    setPoints((prev) => prev.map((p) => ({ ...p, x: +(1 - p.x).toFixed(2) })));
  }, []);

  const handleSwitchY = useCallback(() => {
    setPoints((prev) => prev.map((p) => ({ ...p, y: +(1 - p.y).toFixed(2) })));
  }, []);

  const resetFieldPoints = useCallback(() => {
    setPoints(makePoints(DEFAULT_FIELD_POINTS));
    setActive(null);
  }, []);

  // ── Form / submit ────────────────────────────────────────────────────
  const onChange = useCallback((values) => {
    setField((prev) => ({
      ...prev,
      height: values.height,
      width: values.width,
    }));
  }, []);

  const onSubmit = useCallback(async () => {
    const fieldPoints = points.map((p) => ({ id: p.id, x: p.x, y: p.y }));
    if (![4, 6, 9].includes(fieldPoints.length)) {
      alert("The number of points on the field must be one of 9, 6 or 4!");
      return;
    }
    setSaving(true);
    try {
      await camerasApi.updateField(playgroundId, {
        width: field.width,
        height: field.height,
        points: fieldPoints,
      });
      alert("Results saved");
    } catch (error) {
      alert(`Error: ${error}`);
    } finally {
      setSaving(false);
    }
  }, [playgroundId, points, field]);

  // ── Shape data for rendering ─────────────────────────────────────────
  const shapePoints = useMemo(() => {
    if (!w || !h || points.length === 0) return [];
    const ordered =
      points.length !== 9
        ? points
        : [
            points[7], points[1], points[0], points[6],
            points[7], points[6], points[5], points[4],
            points[7], points[4], points[3], points[8],
            points[7], points[8], points[2], points[1],
            points[7],
          ];
    return ordered.flatMap((p) => [p.x * w, p.y * h]);
  }, [points, w, h]);

  const generateCustomShape = useCallback(
    (context, shape) => {
      if (!w || !h || points.length === 0) return;
      context.beginPath();
      let pts = [...points];
      context.moveTo(pts[0].x * w, pts[0].y * h);
      if (pts.length === 9) {
        pts = [pts[0], pts[1], pts[2], pts[8], ...pts.slice(3, 7)];
      }
      for (const pt of pts) {
        context.lineTo(pt.x * w, pt.y * h);
      }
      context.closePath();
      context.fillStrokeShape(shape);
    },
    [points, w, h]
  );

  const activePoint = useMemo(
    () => active && points.find((p) => p.id === active),
    [active, points]
  );

  const pointOptions = useMemo(
    () =>
      editing
        ? points.map((p) => (
            <option key={p.id} value={p.id}>
              {p.desc} [{p.x.toFixed(2)} : {p.y.toFixed(2)}]
            </option>
          ))
        : null,
    [editing, points]
  );

  // ── Render ───────────────────────────────────────────────────────────
  if (loading) return <Preloader />;

  return (
    <Container className="p-3" fluid>
      <Row>
        <Col>
          <div
            ref={containerRef}
            style={{ position: "relative", aspectRatio: "16 / 9", background: "#000" }}
            onPointerDown={editing ? handleContainerPointerDown : undefined}
          >
            {/* Snapshot image */}
            {snapshotUrl && (
              <img
                src={snapshotUrl}
                alt="Camera snapshot"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  pointerEvents: "none",
                }}
              />
            )}

            {/* Konva overlay */}
            {w > 0 && h > 0 && (
              <div style={{ position: "absolute", top: 0, left: 0, width: w, height: h }}>
                <Stage
                  width={w}
                  height={h}
                >
                  <Layer>
                    <Rect
                      name="field-bg"
                      width={w}
                      height={h}
                      fill="#000000"
                      opacity={editing ? 0.15 : 0.3}
                    />
                    <Line
                      points={shapePoints}
                      stroke="white"
                      strokeWidth={lineWidth}
                      tension={0}
                    />
                    <Shape
                      sceneFunc={generateCustomShape}
                      fill="#00D2FF"
                      opacity={0.25}
                    />
                    {points.map((point) => (
                      <Group
                        key={`fp.${point.id}`}
                        id={point.id}
                        draggable={editing}
                        onDragStart={handlePointDragStart}
                        onDragMove={handlePointDragMove}
                        onDragEnd={handlePointDragEnd}
                        onClick={handlePointClick}
                      >
                        <Circle
                          id={point.id}
                          x={toAbsX(point.x)}
                          y={toAbsY(point.y)}
                          radius={actualRadius / 3}
                          hitStrokeWidth={hitWidth}
                          fill={active === point.id ? "white" : point.color}
                          shadowColor="white"
                          shadowOffset={{ x: 1, y: 1 }}
                          shadowBlur={10}
                          shadowOpacity={active === point.id ? 1 : 0}
                        />
                        <Text
                          x={toAbsX(point.x) - actualRadius * 2 - 15}
                          y={toAbsY(point.y) + actualRadius * 2}
                          fill="#ffffff"
                          text={point.desc}
                          fontSize={fontSize}
                          opacity={editing ? 1.0 : 0.0}
                        />
                      </Group>
                    ))}

                    {activePoint && (
                      <Text
                        x={toAbsX(0.01)}
                        y={toAbsY(0.025)}
                        text={`Active point: ${activePoint.desc}`}
                        fill="white"
                        fontSize={fontSize}
                      />
                    )}
                  </Layer>
                </Stage>
              </div>
            )}

            {/* Nipple (joystick) zone — only in editing mode */}
            {editing && (
              <div
                ref={nippleRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: 5,
                }}
              />
            )}

            {/* Flip buttons — top-right corner */}
            <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 4, zIndex: 10 }}>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSwitchY}
                title="Flip vertical"
                style={{ padding: "2px 8px", fontSize: 12 }}
              >
                <FontAwesomeIcon icon={["fas", "exchange-alt"]} style={{ transform: "rotate(90deg)" }} />
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={handleSwitchX}
                title="Flip horizontal"
                style={{ padding: "2px 8px", fontSize: 12 }}
              >
                <FontAwesomeIcon icon={["fas", "exchange-alt"]} />
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col className="d-flex flex-column">
          <Button
            variant="outline-dark"
            className="viewer__button my-2"
            onClick={() => setEditing((v) => !v)}
          >
            Field marking: {editing ? "Editing" : "View"}
          </Button>
          <FieldSubmitForm
            ref={formRef}
            width={field.width}
            height={field.height}
            onChange={onChange}
            onSubmit={onSubmit}
          />
          <Button
            variant="outline-danger"
            className="viewer__button"
            onClick={resetFieldPoints}
          >
            Clear
          </Button>
        </Col>

        <Col>
          <Button
            type="submit"
            variant="outline-primary"
            className="form__form_button confirm-data my-2 w-100"
            disabled={!field.width || !field.height || saving}
            onClick={() => formRef.current?.submit()}
          >
            {saving ? <Spinner size="sm" /> : "Send"}
          </Button>
          <select
            value={active || -1}
            className="form-select my-2 w-100"
            onChange={(e) => setActive(e.target.value)}
            disabled={!editing}
          >
            <option value={-1} disabled hidden>
              Select a point on the field
            </option>
            {pointOptions}
          </select>
          <Button
            variant="outline-success"
            className="w-100"
            onClick={loadSnapshot}
            disabled={snapshotLoading}
          >
            {snapshotLoading ? <Spinner size="sm" /> : "Refresh Snapshot"}
          </Button>
        </Col>
      </Row>
    </Container>
  );
}
